/**
 * Tenant-scoped payroll export policy. Defaults are award-on and provider-agnostic.
 * A missing table or row must not invent Golden Bloom constants.
 */

export const TENANT_PAYROLL_EXPORT_WRITE_MODES = [
  'TIMESHEET',
  'PAYSLIP_WRITE',
  'TIMESHEET_PLUS_PAYSLIP_ALLOWANCES',
] as const;

export type TenantPayrollExportWriteMode = (typeof TENANT_PAYROLL_EXPORT_WRITE_MODES)[number];

export const TENANT_OVERTIME_POLICIES = ['AWARD', 'FOLD_INTO_ORDINARY', 'REJECT'] as const;
export type TenantOvertimePolicy = (typeof TENANT_OVERTIME_POLICIES)[number];

export const TENANT_VEHICLE_KM_PROJECTIONS = ['SINGLE', 'SAME_UNITS'] as const;
export type TenantVehicleKmProjection = (typeof TENANT_VEHICLE_KM_PROJECTIONS)[number];

export const TENANT_FIRST_AID_EXPORT_MODES = ['ENGINE', 'LEAVE_TEMPLATE'] as const;
export type TenantFirstAidExportMode = (typeof TENANT_FIRST_AID_EXPORT_MODES)[number];

export const TENANT_SCHADS_SOURCE_IDENTITIES = ['XERM', 'PCM_SEEDED'] as const;
export type TenantSchadsSourceIdentity = (typeof TENANT_SCHADS_SOURCE_IDENTITIES)[number];

export const TENANT_SHIFT_LOADING_REPRESENTATIONS = [
  'WHOLE_HOURS',
  'LOADING_ONLY',
  'BASE_PLUS_LOADING',
] as const;
export type TenantShiftLoadingRepresentation =
  (typeof TENANT_SHIFT_LOADING_REPRESENTATIONS)[number];

export interface TenantPayrollExportPolicy {
  tenantId: string;
  xeroWriteMode: TenantPayrollExportWriteMode;
  suppressProfileMinimumEngagement: boolean;
  suppressSchadsSleepoverFlanking: boolean;
  overtimePolicy: TenantOvertimePolicy;
  vehicleKmProjection: TenantVehicleKmProjection;
  firstAidExportMode: TenantFirstAidExportMode;
  schadsSourceIdentity: TenantSchadsSourceIdentity;
  shiftLoadingRepresentation: TenantShiftLoadingRepresentation;
}

export const DEFAULT_TENANT_PAYROLL_EXPORT_POLICY: Omit<TenantPayrollExportPolicy, 'tenantId'> = {
  xeroWriteMode: 'TIMESHEET',
  suppressProfileMinimumEngagement: false,
  suppressSchadsSleepoverFlanking: false,
  overtimePolicy: 'AWARD',
  vehicleKmProjection: 'SINGLE',
  firstAidExportMode: 'ENGINE',
  schadsSourceIdentity: 'XERM',
  shiftLoadingRepresentation: 'WHOLE_HOURS',
};

export function inferVehicleKmProjection(
  mappedKmCategories: readonly string[],
): TenantVehicleKmProjection {
  const live = new Set(mappedKmCategories);
  if (live.has('SCHADS_KM_EXEMPT') && live.has('SCHADS_KM_TAXABLE')) return 'SAME_UNITS';
  return 'SINGLE';
}

const PAYSLIP_ALLOWANCE_CATEGORY = /KM|BROKEN_SHIFT|FIRST_AID/i;

export function isPayslipAllowanceWriteMode(
  mode: string | null | undefined,
): mode is Exclude<TenantPayrollExportWriteMode, 'TIMESHEET'> {
  return mode === 'PAYSLIP_WRITE' || mode === 'TIMESHEET_PLUS_PAYSLIP_ALLOWANCES';
}

export function resolveLiveXeroWriteMode(input: {
  policyWriteMode?: TenantPayrollExportWriteMode | null;
  policyRowPresent?: boolean;
  /**
   * True only when the tenant's policy row carries an explicit, audited
   * payslip-write authorisation (authorised at, by and reason all present).
   * Decision: Paul Benson, 6 Sep 2026 — Medea Park's EA pay runs are written
   * to Xero as Statura-priced payslip lines. Without this flag the 12 Aug rule
   * below still holds and an EA tenant stays on timesheets.
   */
  policyAuthorisedPayslipWrite?: boolean;
  hasEaExportItem: boolean;
  tenantHasEa?: boolean;
  mappedEarningsCategories: readonly string[];
}): TenantPayrollExportWriteMode {
  if (
    input.policyRowPresent &&
    input.policyWriteMode === 'PAYSLIP_WRITE' &&
    input.policyAuthorisedPayslipWrite === true
  ) {
    return 'PAYSLIP_WRITE';
  }
  // EA/RAC money path stays on timesheets unless explicitly authorised above.
  // A saved overlay or an unauthorised PAYSLIP_WRITE policy row must not win,
  // and inference must not key off this batch alone.
  if (input.hasEaExportItem || input.tenantHasEa === true) return 'TIMESHEET';
  if (input.policyRowPresent && input.policyWriteMode) return input.policyWriteMode;
  if (
    input.mappedEarningsCategories.some((category) => PAYSLIP_ALLOWANCE_CATEGORY.test(category))
  ) {
    return 'TIMESHEET_PLUS_PAYSLIP_ALLOWANCES';
  }
  return 'TIMESHEET';
}

export function resolveTenantPayrollExportPolicy(
  tenantId: string,
  row:
    | Partial<{
        xero_write_mode: string;
        suppress_profile_minimum_engagement: boolean;
        suppress_schads_sleepover_flanking: boolean;
        overtime_policy: string;
        vehicle_km_projection: string;
        first_aid_export_mode: string;
        schads_source_identity: string;
        shift_loading_representation: string;
      }>
    | null
    | undefined,
): TenantPayrollExportPolicy {
  const defaults = DEFAULT_TENANT_PAYROLL_EXPORT_POLICY;
  return {
    tenantId,
    xeroWriteMode: isWriteMode(row?.xero_write_mode) ? row.xero_write_mode : defaults.xeroWriteMode,
    suppressProfileMinimumEngagement: row?.suppress_profile_minimum_engagement === true,
    suppressSchadsSleepoverFlanking: row?.suppress_schads_sleepover_flanking === true,
    overtimePolicy: isOvertime(row?.overtime_policy)
      ? row.overtime_policy
      : defaults.overtimePolicy,
    vehicleKmProjection: isKm(row?.vehicle_km_projection)
      ? row.vehicle_km_projection
      : defaults.vehicleKmProjection,
    firstAidExportMode: isFirstAid(row?.first_aid_export_mode)
      ? row.first_aid_export_mode
      : defaults.firstAidExportMode,
    schadsSourceIdentity: isIdentity(row?.schads_source_identity)
      ? row.schads_source_identity
      : defaults.schadsSourceIdentity,
    shiftLoadingRepresentation: isLoadingRepresentation(row?.shift_loading_representation)
      ? row.shift_loading_representation
      : defaults.shiftLoadingRepresentation,
  };
}

export function xeroCloseoutHardBlocksSnapshot(
  workers: readonly { blockers: readonly unknown[] }[],
): boolean {
  return workers.some((worker) => worker.blockers.length > 0);
}

function isWriteMode(value: string | undefined): value is TenantPayrollExportWriteMode {
  return TENANT_PAYROLL_EXPORT_WRITE_MODES.includes(value as TenantPayrollExportWriteMode);
}

function isOvertime(value: string | undefined): value is TenantOvertimePolicy {
  return TENANT_OVERTIME_POLICIES.includes(value as TenantOvertimePolicy);
}

function isKm(value: string | undefined): value is TenantVehicleKmProjection {
  return TENANT_VEHICLE_KM_PROJECTIONS.includes(value as TenantVehicleKmProjection);
}

function isFirstAid(value: string | undefined): value is TenantFirstAidExportMode {
  return TENANT_FIRST_AID_EXPORT_MODES.includes(value as TenantFirstAidExportMode);
}

function isIdentity(value: string | undefined): value is TenantSchadsSourceIdentity {
  return TENANT_SCHADS_SOURCE_IDENTITIES.includes(value as TenantSchadsSourceIdentity);
}

function isLoadingRepresentation(
  value: string | undefined,
): value is TenantShiftLoadingRepresentation {
  return TENANT_SHIFT_LOADING_REPRESENTATIONS.includes(value as TenantShiftLoadingRepresentation);
}
